import React from 'react'
import ReactInputMask from 'react-input-mask'
import { CreditCard, Person, PinMap, GlobeAmericas, ShieldCheck, LockFill } from 'react-bootstrap-icons'
import countries from '@pixelpay/sdk-core/lib/assets/countries.json'
import logoCenec from '../assets/cenec_2.svg'
import logoChorotega from '../assets/chorotega_2.svg'
import '../payment.css'

/**
 * UI presentacional compartida del link de pago (básica, pyme abierta y pyme fija).
 *
 * NO contiene lógica de negocio: recibe por props el `register`/`errors`/`handleSubmit`
 * de react-hook-form y los flags/estado desde el componente contenedor. Los nombres de
 * campo y sus validaciones son idénticos a los originales, de modo que la forma de
 * consumir los servicios no cambia en absoluto.
 */
export const PaymentFormLayout = ({
    logoUrl,
    title,
    description,
    register,
    errors,
    handleSubmit,
    onSubmit,
    isInternational,
    setIsInternational,
    showBillingAddress,
    setShowBillingAddress,
    showAdditionalFields,
    stateSelect,
    phoneCodeSelected,
    months,
    years,
    isPosting,
    amountReadOnly = false,
    amountValue,
}) => {
    return (
        <div className="pl-page">
            <div className="pl-card">
                <div className="pl-brandbar"></div>

                <div className="pl-header">
                    {logoUrl
                        ? <img src={logoUrl} alt={`${title || ''} logo`} className="pl-logo" />
                        : <h1 className="pl-title">{title}</h1>}
                </div>

                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                    <div className="pl-body">
                        {logoUrl && title && <h2 className="pl-title text-center mt-2">{title}</h2>}
                        {description && <p className="pl-desc">{description}</p>}

                        {/* Tipo de tarjeta / origen */}
                        <div className="pl-segment" role="group" aria-label="Origen de la tarjeta">
                            <button
                                type="button"
                                className={`pl-seg-btn ${!isInternational ? 'active' : ''}`}
                                onClick={() => setIsInternational(false)}
                            >
                                <PinMap /> Nacional
                            </button>
                            <button
                                type="button"
                                className={`pl-seg-btn ${isInternational ? 'active' : ''}`}
                                onClick={() => setIsInternational(true)}
                            >
                                <GlobeAmericas /> Extranjero
                            </button>
                        </div>

                        {/* Monto fijo (solo lectura) destacado */}
                        {amountReadOnly && (
                            <div className="pl-amount-hero">
                                <div className="cap">Monto a pagar</div>
                                <div className="val">
                                    <small>L.</small>{Number(amountValue || 0).toLocaleString('en-US')}
                                </div>
                                <input
                                    type="number"
                                    readOnly
                                    value={amountValue}
                                    className="d-none"
                                    {...register('amount', { required: 'Monto a pagar es requerido' })}
                                />
                            </div>
                        )}

                        {/* Número de tarjeta */}
                        <div className="mb-3">
                            <div className="input-group">
                                <span className="input-group-text"><CreditCard /></span>
                                <div className="form-floating">
                                    <ReactInputMask
                                        type="text"
                                        mask={"9999 9999 9999 9999"}
                                        maskChar={" "}
                                        id="idNumberCard"
                                        placeholder="Número de tarjeta"
                                        className="form-control"
                                        {...register('number', { required: 'Número de tarjeta es requerido' })}
                                    />
                                    <label htmlFor="idNumberCard">Número de tarjeta</label>
                                </div>
                            </div>
                            {errors.number && <small className="pl-err">{errors.number.message}</small>}
                        </div>

                        {/* Titular */}
                        <div className="mb-3">
                            <div className="input-group">
                                <span className="input-group-text"><Person /></span>
                                <div className="form-floating">
                                    <input
                                        type="text"
                                        id="idCustomerName"
                                        className="form-control"
                                        placeholder="Juan Perez"
                                        {...register('cardholder', { required: 'Nombre del titular de la tarjeta es requerido' })}
                                    />
                                    <label htmlFor="idCustomerName">Nombre del titular de la tarjeta</label>
                                </div>
                            </div>
                            {errors.cardholder && <small className="pl-err">{errors.cardholder.message}</small>}
                        </div>

                        {/* Vencimiento + CVV */}
                        <div className="row g-3 mb-3">
                            <div className="col-4">
                                <div className="form-floating">
                                    <select
                                        className="form-select"
                                        id="selectMonth"
                                        aria-label="Mes de vencimiento"
                                        {...register('expire_month', { required: 'Mes es requerido' })}
                                    >
                                        <option value="">--</option>
                                        {months.map(item => (<option key={`mesExperira${item}`} value={item}>{item}</option>))}
                                    </select>
                                    <label htmlFor="selectMonth">Mes</label>
                                </div>
                                {errors.expire_month && <small className="pl-err">{errors.expire_month.message}</small>}
                            </div>
                            <div className="col-4">
                                <div className="form-floating">
                                    <select
                                        className="form-select"
                                        id="selectYear"
                                        aria-label="Año de vencimiento"
                                        {...register('expire_year', { required: 'Año es requerido' })}
                                    >
                                        <option value="">--</option>
                                        {years.map(item => (<option key={`añoExperira${item}`} value={item}>{item}</option>))}
                                    </select>
                                    <label htmlFor="selectYear">Año</label>
                                </div>
                                {errors.expire_year && <small className="pl-err">{errors.expire_year.message}</small>}
                            </div>
                            <div className="col-4">
                                <div className="form-floating">
                                    <input
                                        type="number"
                                        id="idCVVCustomer"
                                        className="form-control"
                                        placeholder="CVV"
                                        {...register('cvv2', { required: 'CVV es campo requerido' })}
                                    />
                                    <label htmlFor="idCVVCustomer">CVV</label>
                                </div>
                                {errors.cvv2 && <small className="pl-err">{errors.cvv2.message}</small>}
                            </div>
                        </div>

                        {/* Correo */}
                        <div className="mb-3">
                            <div className="form-floating">
                                <input
                                    type="email"
                                    id="idEmailCustomer"
                                    className="form-control"
                                    placeholder="correo@electroni.co"
                                    {...register('email', { required: 'Correo electrónico es requerido' })}
                                />
                                <label htmlFor="idEmailCustomer">Correo electrónico</label>
                            </div>
                            {errors.email && <small className="pl-err">{errors.email.message}</small>}
                        </div>

                        {/* Monto editable */}
                        {!amountReadOnly && (
                            <div className="mb-3">
                                <div className="input-group">
                                    <span className="input-group-text">L.</span>
                                    <div className="form-floating">
                                        <input
                                            type="number"
                                            id="idAmount"
                                            className="form-control fw-bold"
                                            placeholder="Monto a pagar"
                                            {...register('amount', { required: 'Monto a pagar es requerido' })}
                                        />
                                        <label htmlFor="idAmount">Monto a pagar</label>
                                    </div>
                                </div>
                                {errors.amount && <small className="pl-err">{errors.amount.message}</small>}
                            </div>
                        )}

                        {/* Checkbox facturación (solo nacional) */}
                        {!isInternational && (
                            <label className="pl-check" htmlFor="needInvoice">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="needInvoice"
                                    checked={showBillingAddress}
                                    onChange={(e) => setShowBillingAddress(e.target.checked)}
                                />
                                Deseo ingresar dirección de facturación
                            </label>
                        )}

                        {/* Campos adicionales (extranjero o facturación) */}
                        {showAdditionalFields && (
                            <div className="pl-subcard mb-3">
                                <div className="pl-section-title">
                                    {isInternational ? <GlobeAmericas /> : <PinMap />}
                                    {isInternational ? 'Información Extranjero' : 'Datos de Facturación'}
                                </div>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <select
                                                className="form-select"
                                                id="idCountry"
                                                {...register('country', { required: showAdditionalFields ? 'País es requerido' : false })}
                                            >
                                                <option value="">Seleccione un país</option>
                                                {Object.keys(countries).map(key => (
                                                    <option key={key} value={key}>{countries[key]}</option>
                                                ))}
                                            </select>
                                            <label htmlFor="idCountry">País</label>
                                        </div>
                                        {errors.country && <small className="pl-err">{errors.country.message}</small>}
                                    </div>

                                    <div className="col-md-6">
                                        <div className="form-floating">
                                            <select
                                                className="form-select"
                                                id="idState"
                                                disabled={Object.keys(stateSelect).length === 0}
                                                {...register('state', { required: showAdditionalFields ? 'Estado es requerido' : false })}
                                            >
                                                <option value="">Seleccione un estado</option>
                                                {Object.keys(stateSelect).map(key => (
                                                    <option key={key} value={key}>{stateSelect[key]}</option>
                                                ))}
                                            </select>
                                            <label htmlFor="idState">Estado</label>
                                        </div>
                                        {errors.state && <small className="pl-err">{errors.state.message}</small>}
                                    </div>

                                    <div className="col-12">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Ciudad"
                                            {...register('city', { required: showAdditionalFields ? 'Ciudad es requerida' : false })}
                                        />
                                        {errors.city && <small className="pl-err">{errors.city.message}</small>}
                                    </div>

                                    <div className="col-12">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Dirección"
                                            {...register('address', { required: showAdditionalFields ? 'Dirección es requerida' : false })}
                                        />
                                        {errors.address && <small className="pl-err">{errors.address.message}</small>}
                                    </div>

                                    <div className="col-md-6">
                                        <div className="input-group">
                                            {phoneCodeSelected && <span className="input-group-text">{phoneCodeSelected}</span>}
                                            <input
                                                type="tel"
                                                className="form-control"
                                                placeholder="Número de teléfono"
                                                {...register('phone', { required: showAdditionalFields ? 'Teléfono es requerido' : false })}
                                            />
                                        </div>
                                        {errors.phone && <small className="pl-err">{errors.phone.message}</small>}
                                    </div>

                                    <div className="col-md-6">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Código postal"
                                            {...register('zip', { required: showAdditionalFields ? 'Código postal es requerido' : false })}
                                        />
                                        {errors.zip && <small className="pl-err">{errors.zip.message}</small>}
                                    </div>
                                </div>
                            </div>
                        )}

                        <p className="pl-hnl-note">
                            Los cobros se realizarán en moneda HNL, aunque la tarjeta sea en una moneda diferente.
                        </p>

                        <div className="pl-secure">
                            <LockFill /> Pago seguro y cifrado
                        </div>

                        <button type="submit" className="pl-pay-btn" disabled={isPosting}>
                            {isPosting
                                ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> PROCESANDO...</>
                                : <><ShieldCheck /> REALIZAR PAGO</>}
                        </button>

                        <p className="pl-recaptcha-note">Protegido por reCAPTCHA</p>
                    </div>
                </form>

                <div className="pl-footer">
                    <img src={logoCenec} alt="Logo CENEC" />
                    <span className="pl-divider"></span>
                    <img src={logoChorotega} alt="Logo Cooperativa Chorotega" />
                </div>
            </div>
        </div>
    )
}
